import React, { useEffect, useState, useMemo } from "react";
import SelectOption from "./SelectOption";
import { useFormik } from "formik";
import PrimaryButton from "../PrimaryButton";
import { useDispatch } from "react-redux";
import { store } from "../../../redux/store";
import { createOffer } from "../../../services/offerService";
import GreyButton from "../GreyButton";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { getMerchants } from "../../../services/merchantService";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import Pagination from "../TablePagination";
import debounce from "lodash.debounce";

// Validation schema for merchant selection
const merchantSchema = Yup.object().shape({
  merchant: Yup.string().required("Merchant is required"),
});

const MerchantSelection = ({
  stepIndex,
  setStepIndex,
  selectedMerchant,
  setSelectedMerchant,
  setPayloadData,
  editOfferData,
  isEdit,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Use local state instead of useSelector to avoid reducer execution conflicts
  const [loading, setLoading] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [payload, setPayload] = useState(null);
  
  // Safely update state from Redux store
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        setLoading(state?.offerReducer?.loading || false);
        setOfferData(state?.offerReducer?.createdOffer || null);
      } catch (error) {
        console.warn('MerchantSelection store access error:', error.message);
      }
    };
    
    const timeoutId = setTimeout(updateState, 100);
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        setLoading(state?.offerReducer?.loading || false);
        setOfferData(state?.offerReducer?.createdOffer || null);
      } catch (error) {
        console.warn('MerchantSelection store subscription error:', error.message);
      }
    });
    
    return unsubscribe;
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const formik = useFormik({
    initialValues: {
      merchant: editOfferData?.merchant?.name || "",
    },
    validationSchema: merchantSchema,
    onSubmit: async (values) => {
      if (payload !== null || (isEdit && editOfferData?.merchant)) {
        if (isEdit && !payload) {
          setSelectedMerchant(editOfferData.merchant._id)
          // If in edit mode and no new merchant selected, use existing merchant
          setPayload({
            merchantId: editOfferData.merchant._id,
            id: editOfferData._id,
            offerId: editOfferData._id,
            isCreated: false,
          });
        }
        setStepIndex(stepIndex + 1);
      }
    },
  });

  // Fetch merchants function with pagination and search
  const getMerchantsData = async (search) => {
    const params = {
      page: currentPage,
      search: search || null,
    };

    Object.keys(params).forEach((key) =>
      params[key] === null ? delete params[key] : {}
    );

    const merchantPromise = await dispatch(getMerchants(params))?.unwrap();
    if (merchantPromise?.success === true) {
      setMerchants(merchantPromise?.data || []);
      setTotalPages(merchantPromise?.totalPages || 1);
      setCount(merchantPromise?.count || 0);
      setCurrentPage(merchantPromise?.currentPage || 1);
    }
  };
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        getMerchantsData(query);
      }, 500),
    []
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    getMerchantsData(searchQuery);
  }, [currentPage, searchQuery]);

  useEffect(() => {
    if (editOfferData?.merchant) {
      setSelectedMerchant(editOfferData.merchant);
      formik.setFieldValue("merchant", editOfferData.merchant.businessName);
    }
  }, [editOfferData]);

  useEffect(() => {
    if (!selectedMerchant && editOfferData?.merchant) {
      handleClick(editOfferData?.merchant?._id);
    } else if (!selectedMerchant && offerData?.merchant) {
      handleClick(offerData?.merchant?._id);
    }
  }, [merchants, offerData?.merchant?._id, editOfferData?.merchant?._id, selectedMerchant]);

  useEffect(() => {
    setPayloadData(payload);
  }, [payload]);

  const handleClick = async (id) => {
    if (!id) return;
    const merchant = merchants?.find((merch) => merch?._id === id) || null;
    if (!merchant) return;

    // Set local and global selection
    setSelectedMerchant(merchant);
    dispatch(setCurrentBusiness(merchant));
    formik.setFieldValue("merchant", merchant?.businessName || "");

    // Build payload for next steps
    const newPayload = { merchantId: merchant?._id, isCreated: false };
    const currentOfferId = editOfferData?._id || offerData?._id;
    if (currentOfferId) {
      newPayload.id = currentOfferId;
      newPayload.offerId = currentOfferId;
    }
    setPayload(newPayload);
    // Avoid any backend call here; selection is purely client-side
  };

  const { merchant } = formik.values;
  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = formik;

  const attemptNext = async () => {
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    handleSubmit();
  };

  const isMerchantClicked = (merch) => {
    return selectedMerchant?._id === merch?._id;
  };

  const isBusy = Boolean(loading);
  const nextButtonLabel = isBusy ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>Processing...</span>
    </span>
  ) : (
    "Next"
  );

  return (
    <div className="w-full flex flex-col items-center justify-center mt-20">
      <div className="sm:w-[100%] md:w-[80%] lg:w-[70%] xl:w-[55%]">
        <p className="font-poppins font-[500] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#303030]">
          Select Merchant for this Offer
        </p>
        <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676]">
          Choose the merchant who will manage this offer
        </p>

        {/* Search Section */}
        <div className="mt-5 mb-4">
          <input
            type="text"
            placeholder="Search merchants..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-[50%] px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-poppins text-sm"
          />
        </div>

        {/* Merchants List */}
        <div className="mt-5">
          {isBusy ? (
            <div className="flex justify-center items-center py-10">
              <span className="offer-inline-spinner" />
            </div>
          ) : merchants?.length > 0 ? (
            merchants.map((merch) => (
              <SelectOption
                icon={null}
                key={merch._id}
                clicked={isMerchantClicked(merch)}
                title={merch?.businessName}
                handleClick={() => handleClick(merch?._id)}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 font-poppins">
                {searchQuery
                  ? `No merchants found matching "${searchQuery}"`
                  : "No merchants available"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !isSearching && (
          // <div className="flex justify-center items-center mt-6 space-x-2">
          <Pagination
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
          // </div>
        )}

        {/* Validation Error */}
        {errors?.merchant && touched?.merchant ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-1">
            Merchant is required.
          </span>
        ) : null}

        {/* Action Buttons */}
        <div className="w-full text-end mt-10">
          <GreyButton
            className="w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
            label="Cancel"
            onClick={handleGoBack}
          />
          <PrimaryButton
            className={
              "w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat ms-2"
            }
            label={nextButtonLabel}
            disabled={isBusy}
            onClick={attemptNext}
          />
        </div>
      </div>
    </div>
  );
};

export default MerchantSelection;
